// Central content for the Workpay landing page (light / Tabby-Tamara style)

export const WHY_WORKPAY_LINKS = [
  {
    label: "Workpay vs Bank Transfers",
    to: "/why-workpay/workpay-vs-bank-transfers",
    desc: "Why project payments need more than screenshots and manual follow-ups.",
    icon: "Banknote",
  },
  {
    label: "Workpay vs Other Payment Links",
    to: "/why-workpay/workpay-vs-payment-links",
    desc: "A payment link collects money. Workpay manages the project around it.",
    icon: "Link2",
  },
  {
    label: "Why Vendors Choose Workpay",
    to: "/why-workpay/why-vendors-choose-workpay",
    desc: "Built for vendors who are tired of chasing payments.",
    icon: "Briefcase",
  },
  {
    label: "Customer Protection",
    to: "/why-workpay/customer-protection",
    desc: "Clear milestones, approval flow, and dispute documentation for customers.",
    icon: "ShieldCheck",
  },
];

export const NAV_LINKS = [
  { label: "Why Workpay", type: "dropdown", items: WHY_WORKPAY_LINKS },
  { label: "How it works", href: "#how-it-works", type: "anchor" },
  { label: "For vendors", href: "#for-vendors", type: "anchor" },
  { label: "For customers", href: "#for-customers", type: "anchor" },
  { label: "Pricing", to: "/pricing", type: "route" },
  { label: "About", to: "/about", type: "route" },
  { label: "FAQ", href: "#faq", type: "anchor" },
];

export const PROBLEM_QUOTES = [
  "We'll pay next week.",
  "Boss is not available.",
  "Send the invoice again.",
  "Just one small extra change.",
  "We'll release after final delivery.",
  "Can you reduce the price now?",
];

export const TWO_SIDES = {
  vendor: {
    tag: "For vendors",
    headline: "Get paid with clarity.",
    bullets: [
      "Collect project payments",
      "Track milestones",
      "Reduce payment delays",
      "Keep proof organized",
      "Look more professional",
    ],
  },
  customer: {
    tag: "For customers",
    headline: "Pay without fear.",
    bullets: [
      "See project terms",
      "Track progress",
      "Approve milestones",
      "Raise issues clearly",
      "Pay through Stripe-powered flow",
    ],
  },
};

export const HOW_STEPS = [
  { n: "1", icon: "FilePlus2", title: "Create a project", text: "Add amount, scope, timeline, and milestones." },
  { n: "2", icon: "Send", title: "Send the link", text: "Your customer gets a professional payment page." },
  { n: "3", icon: "Activity", title: "Track the work", text: "Both sides see progress, updates, and approvals." },
  { n: "4", icon: "BadgeCheck", title: "Release with confidence", text: "Payments move based on the agreed workflow." },
];

export const DATA_CARDS = [
  {
    icon: "TrendingDown",
    title: "Late payments hurt cash flow",
    text: "B2B payment reports repeatedly show delayed invoices as a major pressure on smaller businesses.",
  },
  {
    icon: "UserX",
    title: "Freelancers face payment risk",
    text: "Freelancer surveys regularly highlight delayed or unpaid work as a common problem.",
  },
  {
    icon: "ShieldCheck",
    title: "Customers need protection too",
    text: "Customers hesitate to pay upfront when there is no clear scope, milestone, or dispute process.",
  },
];

export const FEATURES = [
  { icon: "Wallet", title: "Upfront payment", text: "Start projects with commitment.", tint: "#ecfdf3", color: "#16a34a" },
  { icon: "Activity", title: "Milestone tracking", text: "Show progress without endless explaining.", tint: "#ecfeff", color: "#0891b2" },
  { icon: "CheckCheck", title: "Approval workflow", text: "Make releases clearer for both sides.", tint: "#eef4fb", color: "#2563eb" },
  { icon: "Scale", title: "Dispute center", text: "Keep issues and proof in one place.", tint: "#f1ecfe", color: "#7c3aed" },
  { icon: "BadgeCheck", title: "Verified profiles", text: "Look professional before the first payment.", tint: "#ecfdf3", color: "#16a34a" },
  { icon: "CreditCard", title: "Stripe-powered checkout", text: "Give customers a familiar payment experience.", tint: "#fef3f2", color: "#e11d48" },
];

export const BEFORE = [
  "WhatsApp promises",
  "Manual invoices",
  "Awkward follow-ups",
  "No proof trail",
  "Payment delays",
  "Customer doubts",
];

export const AFTER = [
  "Clear project page",
  "Payment link",
  "Milestones",
  "Approval flow",
  "Proof organized",
  "Better trust",
];

export const USE_CASES = [
  { name: "Freelancers", line: "Stop chasing clients after sending files.", icon: "Laptop" },
  { name: "Agencies", line: "Track approvals before final delivery.", icon: "Megaphone" },
  { name: "Signage companies", line: "Collect advance payments before production starts.", icon: "Signpost" },
  { name: "Printing companies", line: "Cover material costs before you print.", icon: "Printer" },
  { name: "Event suppliers", line: "Lock terms before the big day.", icon: "PartyPopper" },
  { name: "Web developers", line: "Get paid as the build moves forward.", icon: "Code2" },
  { name: "Consultants", line: "Tie payment to delivered milestones.", icon: "Briefcase" },
  { name: "Creative studios", line: "End the endless 'one more change'.", icon: "Palette" },
  { name: "Maintenance services", line: "Clear terms for every job.", icon: "Wrench" },
];

export const PRICING_INCLUDES = [
  "Priority onboarding",
  "Early vendor pricing",
  "Project payment links",
  "Milestone tracking",
  "Dispute workflow",
  "Stripe-powered payment flow",
];

export const TRUST_POINTS = [
  { icon: "CreditCard", text: "Payments powered by Stripe" },
  { icon: "Lock", text: "Workpay does not store card details" },
  { icon: "FileText", text: "Clear project terms" },
  { icon: "CheckCheck", text: "Milestone approvals" },
  { icon: "Scale", text: "Dispute documentation" },
  { icon: "BadgeCheck", text: "Vendor verification" },
];

export const FAQS = [
  { q: "What is Workpay?", a: "Workpay is a Stripe-powered project payment app for service businesses." },
  { q: "Who is it for?", a: "Vendors, freelancers, agencies, consultants, and project-based businesses." },
  { q: "Is Workpay a payment gateway?", a: "No. Payments are processed by Stripe. Workpay manages the project and milestone workflow." },
  { q: "How does it help vendors?", a: "Vendors can collect payments, track milestones, reduce follow-ups, and organize proof of work." },
  { q: "How does it protect customers?", a: "Customers can see project terms, track progress, approve milestones, and raise issues clearly." },
  { q: "Is Workpay available in UAE?", a: "Workpay is opening early access for selected UAE businesses." },
  { q: "What does Workpay cost?", a: "Simple Payment Links cost 2.9% + AED 1 for a successful UAE domestic-card payment. Milestone Payments add a 1% workflow fee plus 5% VAT — 3.95% + AED 1 in total. International cards and currency conversion may add roughly 1% each." },
];

export const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_milestone-first/artifacts/iwvmscu3_Workpay-2.svg",
  logoMark: "https://customer-assets.emergentagent.com/job_milestone-first/artifacts/gfvcm6gk_WorkPay-Icon.png",
  heroComposite: "https://customer-assets.emergentagent.com/job_milestone-first/artifacts/sdn1mp6k_ChatGPT%20Image%20May%2031%2C%202026%20at%2012_02_39%20AM.png",
  heroMobile: "https://customer-assets.emergentagent.com/job_milestone-first/artifacts/q5txw9yt_ChatGPT%20Image%20May%2031%2C%202026%20at%2012_02_36%20AM.png",
  deckCover: "https://customer-assets.emergentagent.com/job_milestone-first/artifacts/nrio2q7d_ChatGPT%20Image%20May%2031%2C%202026%20at%2009_13_42%20PM.png",
  vendor: "https://customer-assets.emergentagent.com/job_milestone-first/artifacts/uh45wwwu_ChatGPT%20Image%20May%2030%2C%202026%20at%2009_07_50%20PM%20%281%29.png",
  customer: "https://customer-assets.emergentagent.com/job_milestone-first/artifacts/jyizq4lh_Untitled%20design-180.png",
};

// ---------------- Pricing page data ----------------
export const PRICING_CARD_FEATURES = [
  "Project payment links",
  "Milestone tracking",
  "Approval workflow",
  "Dispute center",
  "Vendor profile",
  "Customer payment page",
  "Daily payouts for approved milestones",
  "Stripe-powered payment flow",
];

export const PAYOUT_CARDS = [
  {
    icon: "CalendarClock",
    title: "Approved today. Payout processed daily.",
    text: "Once a milestone is approved and eligible, payout movement is handled on a daily basis according to the payment flow.",
  },
  {
    icon: "ListChecks",
    title: "No more unclear payment status.",
    text: "Track whether a milestone is pending, approved, released, or under review.",
  },
  {
    icon: "Layers",
    title: "Built for project-based work.",
    text: "Use Workpay for advance payments, milestone payments, and clear customer approvals.",
  },
];

export const INCLUDED_FEATURES = [
  { icon: "Link2", title: "Payment links", text: "Send customers a professional project payment page." },
  { icon: "Milestone", title: "Milestones", text: "Break work into clear stages." },
  { icon: "CheckCheck", title: "Customer approvals", text: "Let customers approve progress clearly." },
  { icon: "CalendarClock", title: "Daily payout processing", text: "Approved milestones are transferred on a daily payout basis." },
  { icon: "Scale", title: "Dispute center", text: "Keep issues, proof, and updates organized." },
  { icon: "BadgeCheck", title: "Vendor profile", text: "Look more professional before the first payment." },
  { icon: "Activity", title: "Project tracking", text: "Both sides can see what is happening." },
  { icon: "CreditCard", title: "Stripe-powered checkout", text: "Give customers a familiar, secure payment experience." },
];

export const WHY_EARLY = [
  { icon: "TrendingDown", title: "Lower launch pricing", text: "Join during early access and get preferred launch pricing." },
  { icon: "Rocket", title: "Priority onboarding", text: "Get help setting up your first projects and payment links." },
  { icon: "ShieldCheck", title: "Better client confidence", text: "Customers see a cleaner, more professional project workflow." },
  { icon: "Star", title: "Make it your default", text: "Start training clients to pay through a clearer project payment flow." },
];

export const COMPARE_NORMAL = [
  "Manual follow-ups",
  "Unclear approval",
  "Payment screenshots",
  "No milestone view",
  "No organized proof",
  "Customer confusion",
];

export const COMPARE_WORKPAY = [
  "Professional payment link",
  "Clear project terms",
  "Milestone status",
  "Customer approval flow",
  "Dispute documentation",
  "Daily payouts for approved milestones",
];

export const PRICING_FAQS = [
  { q: "What is the launch price?", a: "Simple Payment Links are 2.9% + AED 1 for a successful UAE domestic-card payment. Milestone Payments total 3.95% + AED 1. International cards and currency conversion may add roughly 1% each." },
  { q: "Are payouts daily?", a: "Approved milestone payouts are processed on a daily basis, subject to the payment flow, account status, and processing timelines." },
  { q: "When does a vendor receive money?", a: "Vendors receive payouts for eligible approved milestones according to the daily payout flow." },
  { q: "Can pricing change later?", a: "Yes. Early access pricing is a launch offer and may change after public launch." },
  { q: "Are there extra charges?", a: "Additional processing charges may apply for international cards, refunds, chargebacks, or currency conversion." },
  { q: "Does Workpay process payments directly?", a: "No. Payments are processed through Stripe. Workpay provides the project, milestone, approval, and trust workflow layer." },
  { q: "Is Workpay a payment gateway?", a: "No. Workpay is not a payment gateway. It is a project payment productivity app powered by Stripe payment infrastructure." },
  { q: "Who can get early pricing?", a: "Selected UAE service businesses and vendors approved during the early access launch." },
];

// ---------------- Why Workpay: vs Bank Transfers ----------------
export const BANK_PAINS = [
  { icon: "FileX", title: "No project page", text: "The payment is separate from the scope, timeline, and deliverables." },
  { icon: "Milestone", title: "No milestone tracking", text: "There is no clear way to show what stage the project is in." },
  { icon: "ImageOff", title: "Screenshot chasing", text: "Vendors still ask customers to send proof, reference numbers, and confirmation messages." },
  { icon: "ThumbsDown", title: "No approval flow", text: "Customers and vendors still argue over what was approved and when." },
  { icon: "MessagesSquare", title: "No organized dispute trail", text: "Issues get buried in chats and emails." },
  { icon: "Frown", title: "No professional client experience", text: "A bank transfer feels manual, not like a serious project workflow." },
];

export const BANK_FLOW = [
  "Project terms", "Payment link", "Milestones", "Customer approval", "Daily payout", "Proof & dispute trail",
];

export const BANK_COMPARE = [
  { label: "Payment collection", a: "Manual transfer", b: "Professional project payment link" },
  { label: "Project terms", a: "Usually in chat or invoice", b: "Clear project page" },
  { label: "Milestones", a: "Not built in", b: "Built into the workflow" },
  { label: "Customer approval", a: "Manual and unclear", b: "Approval flow" },
  { label: "Payment status", a: "Screenshot-based", b: "Trackable status" },
  { label: "Proof of work", a: "Scattered across chats", b: "Organized around the project" },
  { label: "Disputes", a: "Personal and messy", b: "Structured dispute center" },
  { label: "Payouts", a: "Depends on manual payment", b: "Daily payouts for approved milestones" },
];

export const BANK_QUOTES = [
  "Please send transfer screenshot.",
  "Payment is not showing.",
  "Accounts team will check.",
  "We will transfer next week.",
  "Can you finish first?",
];

export const BANK_WITH_WORKPAY = [
  "Customer gets a clear payment page",
  "Vendor sees payment status",
  "Milestones are tracked",
  "Approvals are documented",
  "Approved milestones move to daily payout flow",
];

export const BANK_CUSTOMER_BENEFITS = [
  "Clear project scope",
  "Milestone visibility",
  "Approval steps",
  "Dispute documentation",
  "Stripe-powered payment experience",
];

// ---------------- Why Workpay: vs Payment Links ----------------
export const LINKS_PAINS = [
  { icon: "FileQuestion", title: "No project context", text: "The customer pays, but the project terms live somewhere else." },
  { icon: "EyeOff", title: "No milestone visibility", text: "There is no built-in way to track progress." },
  { icon: "ThumbsDown", title: "No approval workflow", text: "Release decisions happen manually and often emotionally." },
  { icon: "MessagesSquare", title: "No dispute structure", text: "Issues are handled through messy chats and scattered evidence." },
  { icon: "Users", title: "No vendor/customer alignment", text: "Both sides may have different expectations after payment." },
  { icon: "History", title: "No project history", text: "The payment is recorded, but the story of the project is not." },
];

export const LINKS_FLOW = [
  "Create project", "Add milestones", "Send link", "Customer pays", "Track progress", "Approve milestone", "Daily payout",
];

export const LINKS_COMPARE = [
  { label: "Purpose", a: "Collect payment", b: "Manage project payment workflow" },
  { label: "Project details", a: "Not central", b: "Built into the payment page" },
  { label: "Milestones", a: "Not included", b: "Built in" },
  { label: "Progress tracking", a: "Not included", b: "Track project status" },
  { label: "Customer approval", a: "Manual", b: "Structured approval flow" },
  { label: "Vendor protection", a: "Limited workflow support", b: "Payment clarity, proof, and milestones" },
  { label: "Customer protection", a: "Limited project visibility", b: "Project terms, milestone tracking, dispute flow" },
  { label: "Payout flow", a: "Basic payment processing", b: "Daily payouts for approved milestones" },
];

export const LINKS_VENDOR_CARDS = [
  "Start with agreed scope",
  "Collect payment professionally",
  "Track milestones",
  "Keep proof organized",
  "Reduce awkward follow-ups",
  "Move approved milestones to daily payouts",
];

export const LINKS_CUSTOMER_BENEFITS = [
  "See what they are paying for",
  "Track project progress",
  "Approve milestones",
  "Raise issues clearly",
  "Pay through a Stripe-powered flow",
];

// ---------------- Why Workpay: Why Vendors Choose ----------------
export const VENDOR_CHOOSE_QUOTES = [
  "We'll pay next week.",
  "Boss is not available.",
  "Send the invoice again.",
  "Can you do one small extra change?",
  "We will release after final delivery.",
  "Accounts team is checking.",
  "We are not happy, but we cannot explain why.",
];

export const VENDOR_CHOOSE_CARDS = [
  { icon: "Wallet", title: "Collect payment before work begins" },
  { icon: "Milestone", title: "Set clear milestones" },
  { icon: "Activity", title: "Track project progress" },
  { icon: "CheckCheck", title: "Get customer approvals" },
  { icon: "FolderCheck", title: "Keep proof organized" },
  { icon: "CalendarClock", title: "Daily payouts for approved milestones" },
];

// ---------------- Why Workpay: Customer Protection ----------------
export const CUSTOMER_SEE = [
  { icon: "FileText", label: "Project scope" },
  { icon: "BadgeDollarSign", label: "Amount" },
  { icon: "CalendarDays", label: "Timeline" },
  { icon: "Milestone", label: "Milestones" },
  { icon: "BadgeCheck", label: "Vendor details" },
  { icon: "Activity", label: "Payment status" },
  { icon: "CheckCheck", label: "Approval steps" },
];

export const CUSTOMER_PROTECT = [
  { icon: "FileText", title: "Clear project terms" },
  { icon: "Eye", title: "Milestone visibility" },
  { icon: "CheckCheck", title: "Approval workflow" },
  { icon: "Scale", title: "Dispute center" },
  { icon: "FolderCheck", title: "Organized proof" },
  { icon: "CreditCard", title: "Stripe-powered payment flow" },
];

export const CUSTOMER_FAQS = [
  { q: "Why did I receive a Workpay link?", a: "The vendor is using Workpay to create a clearer project payment workflow." },
  { q: "Who processes my payment?", a: "Payments are processed through Stripe." },
  { q: "Can I track the project?", a: "Yes. Workpay helps show project status, milestones, and approvals." },
  { q: "What if there is an issue?", a: "You can raise issues through the dispute workflow and keep communication organized." },
  { q: "Is Workpay a bank or wallet?", a: "No. Workpay is a project payment management software platform." },
];
